import { SaleItemState, SaleReportItem } from "@/types/sales";
import { Button, Flex, Input, TableProps, Tooltip } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import api from "@/apis/order";
import { CommonListParams } from "@/types/common";
import SText from "@/styles/SText";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DataListModal from "./components/DataListModal";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef
} from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import { numFormmater } from "@/pages/Requirement/util";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

dayjs.extend(customParseFormat);

interface SalesEntryFilters {
  keyword: string;
}

const INITIAL_SALES_ENTRY_FILTERS: SalesEntryFilters = {
  keyword: "",
};

const SalesEntry: FC = () => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [list, total, listLoading, { fetchList, updateItem }] = useDataList<
    SaleItemState,
    [CommonListParams<string>]
  >({ fetchApi: api.itemStateList });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SalesEntryFilters>(INITIAL_SALES_ENTRY_FILTERS);
  const { keyword } = draftFilters;
  const [editItem, setEditItem] = useState<SaleItemState | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // 导出全部数据
  const handleExportAll = async (): Promise<SaleItemState[]> => {
    setExportLoading(true);
    setExportProgress(0);
    const allDataList: SaleItemState[] = [];

    try {
      const batchSize = 200; // 每批次获取200条

      // 第一次请求获取总数
      const firstRes = await api.itemStateList({
        body: appliedFilters.keyword,
        pagination: { offset: 0, take: batchSize }
      });

      if (firstRes?.body) {
        allDataList.push(...firstRes.body);
      }

      // 根据返回的 pagination.size 计算总批次数
      const totalSize = firstRes?.pagination?.size ?? 0;
      const batches = Math.ceil(totalSize / batchSize);

      // 继续请求剩余数据
      for (let i = 1; i < batches; i++) {
        const offset = i * batchSize;
        const res = await api.itemStateList({
          body: appliedFilters.keyword,
          pagination: { offset, take: batchSize }
        });
        if (res?.body) {
          allDataList.push(...res.body);
        }
        // 更新进度
        setExportProgress(Math.round(((i + 1) / batches) * 100));
      }
      return allDataList;
    } catch (error) {
      console.error(t("common.exportErr"), error);
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const getProgressEle = (
    item: SaleReportItem | null | undefined,
    key: keyof SaleReportItem
  ) => {
    return (
      <SText
        key={item?.id + key}
        text={numFormmater((item ? item[key] : 0) as number)}
      />
    );
  };

  // 导出列配置
  const exportColumns = (t: any) => [
    { key: "sale.innerNumber", label: t("sale.innerNumber") },
    { key: "product.code", label: t("product.code") },
    { key: "product.name", label: t("product.name") },
    { key: "product.brand1", label: t("product.brand1") },
    { key: "sale.saleTotalQuantity", label: t("sale.saleTotalQuantity") },
    { key: "sale.smt", label: "SMT" },
    { key: "sale.dip", label: "DIP" },
    { key: "sale.test1", label: "TEST1" },
    { key: "sale.assy", label: "ASSY" },
    { key: "sale.test2", label: "TEST2" },
    { key: "sale.pack", label: "PACK" },
    { key: "sale.inStock", label: t("sale.inStock") },
    { key: "sale.outPro", label: t("sale.outPro") }
  ];

  // 默认选中的列
  const defaultSelectedColumns = (t: any) => [
    "sale.innerNumber",
    "product.code",
    "product.name",
    "sale.saleTotalQuantity",
    "sale.smt",
    "sale.dip",
    "sale.test1"
  ];

  // 导出数据映射函数
  const exportDataMapper = (item: SaleItemState, t: any) => ({
    [t("sale.innerNumber")]: item?.order?.innerNumber || "",
    [t("product.code")]: item?.product?.code || "",
    [t("product.name")]: item?.product?.name || "",
    [t("product.brand1")]: item?.product?.model || "",
    [t("sale.saleTotalQuantity")]: item?.quantity || 0,
    SMT: item?.prodState?.t1 || 0,
    DIP: item?.prodState?.t2 || 0,
    TEST1: item?.prodState?.t3 || 0,
    ASSY: item?.prodState?.t4 || 0,
    TEST2: item?.prodState?.t5 || 0,
    PACK: item?.prodState?.t6 || 0,
    [t("sale.inStock")]: item?.prodState?.t7 || 0,
    [t("sale.outPro")]: item?.prodState?.t8 || 0
  });

  const columns: TableProps<SaleItemState>["columns"] = [
    {
      title: t("sale.orderInfo"),
      children: [
        {
          title: <SText text={t("sale.innerNumber")} />,
          key: "orderInnerNumber",
          dataIndex: ["order", "innerNumber"],
          fixed: "left",
          width: 120,
          sorter: (a, b) => {
            const valA = a?.order?.innerNumber ?? "";
            const valB = b?.order?.innerNumber ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText
              key={item.id + "innerNumber"}
              text={item.order?.innerNumber ?? ""}
            />
          )
        }
      ]
    },
    {
      title: t("sale.proInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          key: "productCode",
          dataIndex: ["product", "code"],
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.code ?? ""}
            />
          )
        },
        {
          title: <SText text={t("product.name")} />,
          key: "productName",
          width: 140,
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.name ?? ""}
            />
          )
        },
        {
          title: <SText text={t("product.brand1")} />,
          key: "model",
          width: 140,
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.model ?? ""}
            />
          )
        }
      ]
    },
    {
      title: <SText text={t("sale.reportInfo")} />,
      children: [
        {
          title: <SText text={t("sale.saleTotalQuantity")} />,
          key: "quantity",
          width: 90,
          render: (_, item) => (
            <SText
              key={item.id + "quantity"}
              text={numFormmater(Number(item.quantity ?? 0))}
            />
          )
        },
        {
          title: <SText text="SMT" />,
          key: "reportT1",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t1")
        },
        {
          title: <SText text="DIP" />,
          key: "reportT2",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t2")
        },
        {
          title: <SText text="TEST1" />,
          key: "reportT3",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t3")
        },
        {
          title: <SText text="ASSY" />,
          key: "reportT4",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t4")
        },
        {
          title: <SText text="TEST2" />,
          key: "reportT5",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t5")
        },
        {
          title: <SText text="PACK" />,
          key: "reportT6",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t6")
        },
        {
          title: <SText text={t("sale.inStock")} />,
          key: "reportT7",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t7")
        },
        {
          title: <SText text={t("sale.outPro")} />,
          key: "reportT8",
          width: 90,
          render: (_, item) => getProgressEle(item.prodState, "t8")
        }
      ]
    },
    {
      title: <SText text={t("common.opt")} />,
      fixed: "right",
      width: 200,
      key: "opt",
      render: (_, record) => (
        <Button type="link" onClick={() => setEditItem(record)}>
          {t("sale.addEdit")}
        </Button>
      )
    }
  ];

  const fetch = (offset: number, filters: SalesEntryFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList({
      body: filters.keyword,
      pagination: { offset, take }
    }).catch(err => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  useEffect(() => {
    fetch(0, INITIAL_SALES_ENTRY_FILTERS);
  }, []);

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_SALES_ENTRY_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex style={{ width: "100%", marginBottom: 20 }} justify="space-between">
      <Flex gap={10}>
        <ExportExcelButton<SaleItemState>
          list={list}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="order_entry.xlsx"
          sheetName={t("layout.orderEntry")}
        />
        <ExportExcelButton<SaleItemState>
          list={list}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="order_entry_all.xlsx"
          sheetName={t("layout.orderEntry")}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("sale.productionSearchTip")}>
              <Input
                placeholder={t("sale.productionSearchTip")}
                style={{ width: 200 }}
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                onPressEnter={submit}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1400}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      {editItem && (
        <DataListModal
          open={!!editItem}
          id={editItem.id}
          list={editItem.prodStateHis ?? []}
          handleCancel={list => {
            const { id, ...info } = list.reduce(
              (prev, cur) => {
                return {
                  ...cur,
                  t1: prev.t1 + cur.t1,
                  t2: prev.t2 + cur.t2,
                  t3: prev.t3 + cur.t3,
                  t4: prev.t4 + cur.t4,
                  t5: prev.t5 + cur.t5,
                  t6: prev.t6 + cur.t6,
                  t7: prev.t7 + cur.t7,
                  t8: prev.t8 + cur.t8,
                  t9: prev.t9 + cur.t9,
                  t10: prev.t10 + cur.t10,
                  t11: prev.t11 + cur.t11,
                  t12: prev.t12 + cur.t12
                };
              },
              {
                id: "",
                itemId: editItem.id,
                t1: 0,
                t2: 0,
                t3: 0,
                t4: 0,
                t5: 0,
                t6: 0,
                t7: 0,
                t8: 0,
                t9: 0,
                t10: 0,
                t11: 0,
                t12: 0
              }
            );
            updateItem({
              ...editItem,
              prodStateHis: list,
              prodState: {
                id: "",
                ...info
              }
            });
            setEditItem(null);
          }}
        />
      )}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default SalesEntry;
